import { z } from 'zod';

import { idSchema, isoDateTimeSchema, paginated, paginationQuerySchema } from '../common';
import { mediaObjectKindSchema, mediaObjectStatusSchema } from '../enums';
import { queryBooleanSchema } from './posts';

/**
 * `/media` DTOs.
 *
 * Bytes never pass through the API: the client asks for a signed URL and talks
 * to the object store directly. Postgres holds the key and the metadata only.
 *
 * Retention follows `kind`: comment-template images live as long as their
 * template, while diagnostic screenshots and DOM snapshots — which come off a
 * user's machine — carry an `expiresAt` and are swept after 7-30 days.
 */

/** `type/subtype`, lower-case. The server also checks it against an allowlist
 *  per `kind`, so a valid-looking MIME for the wrong kind is still refused. */
export const mimeTypeSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z]+\/[a-z0-9][a-z0-9.+-]*$/, 'MIME type không hợp lệ')
  .max(120);

/** No path separators: the caller names a file, never a location. */
export const uploadFileNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(255)
  .regex(/^[^/\\:*?"<>|]+$/, 'Tên file chứa ký tự không hợp lệ');

export const mediaObjectSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  kind: mediaObjectKindSchema,
  status: mediaObjectStatusSchema,
  /** Storage key; opaque to the client, which only ever uses signed URLs. */
  objectKey: z.string(),
  fileName: z.string(),
  mimeType: mimeTypeSchema,
  sizeBytes: z.number().int().min(0),
  checksum: z.string().nullable(),
  width: z.number().int().min(0).nullable(),
  height: z.number().int().min(0).nullable(),
  createdAt: isoDateTimeSchema,
  /** Null for objects with no retention limit (template images). */
  expiresAt: isoDateTimeSchema.nullable(),
});
export type MediaObject = z.infer<typeof mediaObjectSchema>;

// ---------------------------------------------------------------------------
// POST /media/signed-upload
// ---------------------------------------------------------------------------

export const signedUploadRequestSchema = z.object({
  kind: mediaObjectKindSchema,
  mimeType: mimeTypeSchema,
  /** Declared up front so the size limit is enforced before the upload starts;
   *  the storage policy re-enforces it on the signed request itself. */
  sizeBytes: z.number().int().min(1),
  fileName: uploadFileNameSchema,
  projectId: idSchema.optional(),
});
export type SignedUploadRequest = z.infer<typeof signedUploadRequestSchema>;

export const signedUploadResponseSchema = z.object({
  mediaObjectId: idSchema,
  /** Single-use PUT target on the object store. */
  uploadUrl: z.string().url(),
  objectKey: z.string(),
  expiresAt: isoDateTimeSchema,
  /**
   * Headers the client MUST replay verbatim on the PUT — they are part of the
   * signature (content type, checksum, storage class). Omitting one makes the
   * store reject the upload rather than accept a different object.
   */
  requiredHeaders: z.record(z.string(), z.string()),
  maxSizeBytes: z.number().int().min(1),
});
export type SignedUploadResponse = z.infer<typeof signedUploadResponseSchema>;

/** Called after the PUT so the object flips from PENDING_UPLOAD to READY. */
export const confirmUploadRequestSchema = z.object({
  checksum: z.string().trim().max(128).optional(),
  sizeBytes: z.number().int().min(0).optional(),
});
export type ConfirmUploadRequest = z.infer<typeof confirmUploadRequestSchema>;

export const confirmUploadResponseSchema = z.object({
  media: mediaObjectSchema,
});
export type ConfirmUploadResponse = z.infer<typeof confirmUploadResponseSchema>;

// ---------------------------------------------------------------------------
// GET /media/:id/signed-url
// ---------------------------------------------------------------------------

export const signedUrlQuerySchema = z.object({
  /** Clamped server-side; a long-lived link is a leak with a timer on it. */
  ttlSeconds: z.coerce.number().int().min(30).max(3_600).default(300),
  download: queryBooleanSchema.default(false),
});
export type SignedUrlQuery = z.infer<typeof signedUrlQuerySchema>;

export const signedUrlResponseSchema = z.object({
  mediaObjectId: idSchema,
  url: z.string().url(),
  expiresAt: isoDateTimeSchema,
  kind: mediaObjectKindSchema,
  status: mediaObjectStatusSchema,
  mimeType: mimeTypeSchema,
  sizeBytes: z.number().int().min(0),
  fileName: z.string(),
});
export type SignedUrlResponse = z.infer<typeof signedUrlResponseSchema>;

// ---------------------------------------------------------------------------
// GET /media  ·  DELETE /media/:id
// ---------------------------------------------------------------------------

export const mediaListQuerySchema = paginationQuerySchema.extend({
  kind: z.union([mediaObjectKindSchema, z.array(mediaObjectKindSchema)]).optional(),
  status: mediaObjectStatusSchema.optional(),
  projectId: idSchema.optional(),
});
export type MediaListQuery = z.infer<typeof mediaListQuerySchema>;

export const mediaListResponseSchema = paginated(mediaObjectSchema);
export type MediaListResponse = z.infer<typeof mediaListResponseSchema>;

export const deleteMediaResponseSchema = z.object({
  mediaObjectId: idSchema,
  status: mediaObjectStatusSchema,
  deletedAt: isoDateTimeSchema,
});
export type DeleteMediaResponse = z.infer<typeof deleteMediaResponseSchema>;
